import React, { createContext, useContext } from 'react';
import { Table } from 'antd';

import { format2FullTimeStr } from '@/utils/date';
import { getOccDuration } from '@/utils';
import { CertIDType, OccType } from '@/config/constant';
import { PortalInstance } from '@/components/PortalConfirm';
import ReserveService from '@/api/reserve';
import { getMemberLevelListWithCache } from '@/api/cacheRequest';

// 入账类型
export enum PostTypeEnum {
  Consumption = 0, // 消费
  Payment = 1, // 付款
  Adjustment = 2, // 冲调
  Discount = 3, // 优惠
  Refund = 4, // 退款
}

export interface BillInfo {
  resvNo: string; // 预订单号
  roomNo: string; // 	房号
  postType: PostTypeEnum; // 入账类型，0消费 1付款 2冲调 3优惠 4退款
  subjectCodeName: string; // 科目名称
  detail: string; // 详情
  amount: number; // 金额
  createTime: number; // 创建时间
  status: number; // 结账状态【0未结 1已结】
  remark: string; // 	账单备注
  subs: BillInfo[];
}

// 详细数据
export interface RollbackData {
  extensionId: number; // 续住id
  resvNo: string; // 预订单号
  name: string; // 预订人姓名
  certType: CertIDType; // 证件类型
  certNo: string; // 证件号
  mobile: string; // 预订人手机号
  email: string; // 预订人邮箱
  memberLevel: number; // 会员等级
  memberLevelMap: Record<string, string>; // 会员等级名称map
  occType: OccType; // 入住类型：日租房/钟点房
  expectedArrivalTime: number; // 预抵日期
  occDuration: number; // 入住时长
  expectedDepartureTime: number; // 预离日期
  sourceChannelName: string; // 客源渠道名称
  priceStrategyName: string; // 价格策略名称
  channelOrderNo: string; // 渠道单号
  suborders: {
    resvNo: string; // 接待单号
    roomTypeName: string; // 房型名称
    rollbackRoomTypeName: string; // 返还房型名称
    roomNo: string; // 	房号
    rollbackRoomNo: string; // 返还房号
    rentDayInfos: {
      rentDate: string; // 日期
      price: number; // 价格：分
    }[];
  }[];
  bills: BillInfo[];
  remark: string;
  checkinRemark: string;
}

export const DetailContext = createContext<RollbackData>(null);

// 详情
export const useDetailData = () => {
  return useContext(DetailContext);
};

// 校验订单状态
export const checkOrderStatus = (id: number, portal: PortalInstance) => {
  return new Promise<void>((resolve, reject) => {
    ReserveService.getModifiedOrderList(id)
      .then((res) => {
        if (res.data?.length) {
          // 订单有修改
          const columns = [
            { title: '入住房号', dataIndex: 'roomNo' },
            {
              title: '原入住时长',
              key: 'occDuration',
              render: (record) =>
                getOccDuration({
                  type: record.occType,
                  duration: record.occDuration,
                  multi: false,
                }),
            },
            { title: '原预离时间', dataIndex: 'expectedDepartureTime', render: format2FullTimeStr },
          ];

          const portalHandler = () => {
            reject();
            portal.closeDrawer();
          };

          portal.showDrawer({
            title: '无法返还预订',
            content: (
              <div>
                <p style={{ marginBottom: 12 }}>
                  该订单的入住时长或预离时间已被修改,无法继续操作,可以先通过入住修改功能改回下述信息后再试
                </p>
                <Table columns={columns} dataSource={res.data} pagination={false} />
              </div>
            ),
            confirmText: '确定',
            onCancel: portalHandler,
            onConfirm: portalHandler,
          });
        } else {
          resolve();
        }
      })
      .catch(reject);
  });
};

// 获取订单详情
export const getOrderDetail = async (id) => {
  const [detailRes, levelRes] = await Promise.all([
    ReserveService.getRollbackOrderDetail(id),
    getMemberLevelListWithCache().catch(() => null),
  ]);

  const levelNameMap: Record<string, string> = {};
  if (levelRes?.data) {
    levelRes.data.forEach((item) => {
      levelNameMap[item.level] = item.name;
    });
  }

  const result: RollbackData = {
    ...detailRes.data,
    extensionId: id,
    memberLevelMap: levelNameMap,
  };
  return result;
};
